import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { Client } from 'src/app/types';
import { AppPipes } from '../../pipes';
import { AuthService } from '../../services/auth/auth.service';
import { NotificationService } from '../../services/notification/notification.service';
import { QueryParamsService } from '../../services/query-params/query-params.service';
import { SharedService } from '../../services/shared-service/shared.service';
import { StorageService } from '../../services/storage/storage.service';
import { TranslateService } from '../../services/translation/translate.service';
import { WINDOW } from '../../services/window/window.service';
import { debounce, debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-navigation-bar',
    templateUrl: './navigation-bar.component.html',
    styleUrls: [
        './navigation-bar.component.less',
        './navigation-bar.component-desktop-medium.less',
        './navigation-bar.component-desktop-small.less',
        './navigation-bar.component-tablet.less',
        './navigation-bar.component-phone-big.less',
        './navigation-bar.component-phone-small.less',
    ],
    imports: [
        CommonModule,
        MatToolbarModule,
        MatButtonModule,
        MatSelectModule,
        MatIconModule,
        MatSidenavModule,
        AppPipes,
    ]
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  @Input() clientObject!: Client;
  public selectedLanguage = this.translate.language;
  public selectedCurrency = '';
  public currentLang?: string;
  public currentUrl?: string;
  public loginLink: string = `${this.selectedLanguage}/login`;
  public isLoggedIn = false;
  public showMainMenu = true;
  private isLoggedInObservable = toObservable(this.authService.isLoggedIn);
  private authDataObservable = toObservable(this.authService.authData);
  private destroy$ = new Subject();

  constructor(
    @Inject(WINDOW) public window: Window,
    private router: Router,
    private renderer: Renderer2,
    private translate: TranslateService,
    private queryParamsService: QueryParamsService,
    private notificationService: NotificationService,
    private sharedService: SharedService,
    private storageService: StorageService,
    public authService: AuthService,
  ) {
    if (this.sharedService.isBrowser()) {
      this.watchAuthStatus();
    } else {
      afterNextRender(() => {
        this.watchAuthStatus();
      })
    }
    
  }

  watchAuthStatus() {
    this.isLoggedInObservable.pipe(
      takeUntil(this.destroy$),
    ).subscribe((val) => {
      this.isLoggedIn = this.authService.isLoggedIn();
      this.showMainMenu = !this.authService.verifyProcessPending();
    });

    this.authDataObservable.pipe(
      takeUntil(this.destroy$),
    ).subscribe((val) => {
      this.isLoggedIn = this.authService.isLoggedIn();
      this.showMainMenu = !this.authService.verifyProcessPending();
    });
  }
  ngOnInit(): void {
    this.selectedCurrency = this.translate.currency;
    this.currentLang = this.translate.language;
    this.currentUrl = this.router.url;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  goToHomePage() {
    this.router.navigate([`${this.selectedLanguage}/home`]).then();
    this.scrollUp();
  }

  getRouter(router: any) {
    return router && this.router.url.toString().includes(router.toString())
      ? true
      : false;
  }

  useLanguage(lang: any) {
    const splitArray = this.router.url.split('/');
    splitArray[1] = lang;
    this.storageService.set('selectedLanguage', lang);
    this.window.location.href = splitArray.join('/');
  }

  useCurrency(currency: string) {
    this.translate.updateCurrentSelectedCurrency(currency);
  }

  scrollUp() {
    if (this.sharedService.isBrowser()) {
      this.window.scrollTo(0, 0);
    }
    this.queryParamsService.updateSharedNavigationChangedState(true);
  }

  checkForKbAutoRoutes(item: any) {
    if (this.clientObject.clientName === 'KB Auto') {
      let kbAutoPageMap = this.sharedService.kbAutoPageObject.find(
        (item: any) => item.lang === this.currentLang
      );
      if (item.link.includes('/about-us')) {
        let item = kbAutoPageMap?.pages.find(
          (page: any) => page.key === 'aboutus'
        );
        if (item) {
          const a = this.renderer.createElement('a');
          this.renderer.setAttribute(a, 'href', item.link);
          a.click();
        }
      } else if (item.link.includes('/newsblog')) {
        let item = kbAutoPageMap?.pages.find(
          (page: any) => page.key === 'newsblog'
        );
        if (item) {
          const a = this.renderer.createElement('a');
          this.renderer.setAttribute(a, 'href', item.link);
          a.click();
        }
      } else if (item.link.includes('/start-selling')) {
        let item = kbAutoPageMap?.pages.find(
          (page: any) => page.key === 'startselling'
        );
        if (item) {
          const a = this.renderer.createElement('a');
          this.renderer.setAttribute(a, 'href', item.link);
          a.click();
        }
      } else if (item.link.includes('/home')) {
        let item = kbAutoPageMap?.pages.find(
          (page: any) => page.key === 'home'
        );
        if (item) {
          const a = this.renderer.createElement('a');
          this.renderer.setAttribute(a, 'href', item.link);
          a.click();
        }
      }
    } else {
      this.goToRoute(item);
    }
  }

  goToRoute(item: any) {
    if (!item.isCustomPage && !item.isInternal) {
      const a = this.renderer.createElement('a');
      this.renderer.setAttribute(a, 'target', '_blank');
      this.renderer.setAttribute(a, 'href', item.link);
      a.click();
    } else {
      this.scrollUp();
      const url = `/${this.currentLang}/${
        item.isCustomPage ? '/pages' + item.link : item.link
      }`;
      if (item.isCustomPage) {
        this.notificationService.setCurrentSelectedCustomPage(item);
      }
      this.router.navigate([url]);
    }
  }

  public signIn(): void {
    this.router.navigate([`${this.selectedLanguage}/login`]);
  }

  public signOut(): void {
    this.authService.signOut();
    this.notificationService.showCustomMessage(
      '',
      'Successfully signed out.',
      'success-snackbar'
    );
    this.router.navigate([`${this.selectedLanguage}/home`]);
  }
}
